import { z } from 'zod';

/*
 * A conta de reembolso do próprio viajante (`/profile/refund-account*` do app-integration): a
 * forma ÚNICA onde caem os reembolsos aprovados, de KM e da carteira. Vive no legado
 * (business-service `userBankAccount`): uma chave Pix OU agência e conta, sempre no CPF do
 * cadastro. Trocar substitui; não existe "remover".
 */

/** Tipos de chave do legado (`BusinessPixTypeEnum`). `EVP` = chave aleatória. */
export const RefundPixTypeSchema = z.enum(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP']);
export type RefundPixType = z.infer<typeof RefundPixTypeSchema>;

/** Tipos de conta do legado (`BusinessAccountTypeEnum`). A tela grava só corrente ou poupança,
 *  mas a leitura devolve o que o cadastro tiver (o Admin grava os quatro). */
export const RefundBankAccountTypeSchema = z.enum(['CURRENT', 'SAVINGS', 'PAYMENT', 'SALARY']);
export type RefundBankAccountType = z.infer<typeof RefundBankAccountTypeSchema>;

/** Um banco do catálogo do legado (`businessListBank`): `GET /profile/banks?q=`. */
export const RefundBankSchema = z.object({ code: z.string().min(1), name: z.string().min(1) });
export type RefundBank = z.infer<typeof RefundBankSchema>;

/** `GET /profile/refund-account` — a forma cadastrada, MASCARADA. `account: null` = nenhuma
 *  (o KM e a carteira barram o envio). `holderName` é o titular que o legado grava (o nome do
 *  cadastro), nunca digitado. */
export const RefundAccountViewSchema = z.object({
  account: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('pix'), pixType: RefundPixTypeSchema, keyMasked: z.string() }),
      z.object({
        kind: z.literal('bank_account'),
        bank: RefundBankSchema.nullable(),
        accountType: RefundBankAccountTypeSchema.nullable(),
        agencyMasked: z.string(),
        accountMasked: z.string(),
      }),
    ])
    .nullable(),
  holderName: z.string().nullable(),
});
export type RefundAccountView = z.infer<typeof RefundAccountViewSchema>;

/** `GET /profile/refund-account/reveal` — a mesma forma, CRUA (tier-3, auditada, `no-store`). */
export const RefundAccountRevealSchema = z.object({
  account: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('pix'), pixType: RefundPixTypeSchema, key: z.string() }),
      z.object({
        kind: z.literal('bank_account'),
        bank: RefundBankSchema.nullable(),
        accountType: RefundBankAccountTypeSchema.nullable(),
        agency: z.string(),
        account: z.string(),
        digit: z.string(),
      }),
    ])
    .nullable(),
});
export type RefundAccountReveal = z.infer<typeof RefundAccountRevealSchema>;

/** A chave como o viajante digitou e o tipo que a tela identificou (`readPixKey`). O BFF confere a
 *  forma e normaliza: CPF/CNPJ em dígitos, celular em E.164 (`+55…`), e-mail em minúsculas. */
const PixKeyInputSchema = z.object({
  pixType: RefundPixTypeSchema,
  key: z.string().trim().min(1).max(100),
});

/** `POST /profile/refund-account/pix-check` — consulta a chave no DICT SEM abrir pagamento. */
export const RefundPixCheckRequestSchema = PixKeyInputSchema.strict();
export type RefundPixCheckRequest = z.infer<typeof RefundPixCheckRequestSchema>;

/** O dono da chave, para o bloco «Nome · Instituição / Conferido no Pix». `documentMasked` vem
 *  mascarado do DICT (CPF) ou pelo BFF (CNPJ). `ownedByTraveler: false` é o `holder_mismatch`
 *  que o Salvar devolveria. */
export const RefundPixCheckSchema = z.object({
  holderName: z.string().nullable(),
  institution: z.string().nullable(),
  documentMasked: z.string().nullable(),
  ownedByTraveler: z.boolean(),
});
export type RefundPixCheck = z.infer<typeof RefundPixCheckSchema>;

/** Agência e conta: só dígitos (a conta sem o dígito verificador, que vai à parte). */
const RefundBankAccountInputSchema = z
  .object({
    kind: z.literal('bank_account'),
    bank: RefundBankSchema,
    accountType: z.enum(['CURRENT', 'SAVINGS']),
    agency: z.string().regex(/^\d{1,5}$/),
    account: z.string().regex(/^\d{1,13}$/),
    digit: z.string().regex(/^[\dXx]$/),
  })
  .strict();

/** `PUT /profile/refund-account` — grava a forma, que substitui a anterior. */
export const RefundAccountWriteSchema = z.discriminatedUnion('kind', [
  PixKeyInputSchema.extend({ kind: z.literal('pix') }).strict(),
  RefundBankAccountInputSchema,
]);
export type RefundAccountWrite = z.infer<typeof RefundAccountWriteSchema>;

/** As recusas do fio. `invalid_request`: corpo fora do schema (a chave fora da forma do tipo
 *  inclusive); `invalid_account`: agência, conta ou dígito fora da forma, ou recusados pelo
 *  legado; `pix_key_not_found`: a chave não está no DICT; `holder_mismatch`: a chave não é do CPF
 *  do viajante; `document_missing`: o cadastro não tem CPF (sem ele não há titular a conferir);
 *  `rate_limited`: o viajante passou do limite de consultas ao DICT (nada foi consultado);
 *  `unavailable`: o legado ou o DICT não respondeu. */
export const REFUND_ACCOUNT_ERROR_CODES = [
  'missing_user_identity',
  'not_found',
  'invalid_request',
  'invalid_account',
  'pix_key_not_found',
  'holder_mismatch',
  'document_missing',
  'rate_limited',
  'unavailable',
] as const;
export type RefundAccountErrorCode = (typeof REFUND_ACCOUNT_ERROR_CODES)[number];
